/**
 * GoldenSignalsPage — the permanent library of finalised signals.
 *
 * WHY IT EXISTS. Combinations found while testing indicators on the Charts page
 * had nowhere to live: their definitions existed only in a source-file header,
 * or not at all, and were lost. This is the catalogue — one entry per signal,
 * with the exact conditions that produce it, structured enough that a strategy
 * can later be composed from several of them.
 *
 * IT IS A CATALOGUE. There is no Run, no Scan, no Execute and no Activate on
 * this page, and the API behind it exposes none. That matters more than usual
 * because this renders as a tab BESIDE the Strategies tab, whose cards read
 * ACTIVE / PAUSED and really do place orders. A probe
 * (scripts/verify-golden-signals.js) asserts no such control ever appears here.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Button, Chip, Divider, IconButton, InputAdornment, List, ListItemButton,
  MenuItem, Paper, Stack, TextField, Tooltip, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import RestoreIcon from '@mui/icons-material/Restore';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAppTheme } from '../hooks/useAppTheme';
import { usePersistedState } from '../hooks/usePersistedState';
import { useConfirm } from '../components/common/ConfirmDialog';
import EmptyState from '../components/common/EmptyState';
import SignalDetail from '../components/signals/SignalDetail';
import SignalEditorDialog from '../components/signals/SignalEditorDialog';
import { CHART_OVERLAY_CATALOGUE } from '../components/signals/overlayCatalogue';
import { strategies } from '../utils/strategies';
import {
  goldenSignalsService, GoldenSignalRow, GoldenSignalDraft,
  GS_DIRECTIONS, GS_STATUSES, GS_STATUS_HELP,
} from '../services/goldenSignals.service';

interface Props { embedded?: boolean }

const GoldenSignalsPage: React.FC<Props> = ({ embedded }) => {
  const { C } = useAppTheme();
  const confirm = useConfirm();
  const location = useLocation();

  const [rows, setRows] = useState<GoldenSignalRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = usePersistedState<string>('goldenSignals.q', '');
  const [status, setStatus] = usePersistedState<string>('goldenSignals.status', 'ALL');
  const [direction, setDirection] = usePersistedState<string>('goldenSignals.direction', 'ALL');
  const [tag, setTag] = usePersistedState<string>('goldenSignals.tag', 'ALL');
  const [selectedId, setSelectedId] = usePersistedState<number | null>('goldenSignals.selected', null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState<GoldenSignalRow | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const list = await goldenSignalsService.list();
      setRows(list);
    } catch (e: any) {
      toast.error(e?.response?.data?.error?.message || 'Failed to load your signal library');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const allTags = useMemo(() => {
    const s = new Set<string>();
    rows.forEach(r => (r.tags || []).forEach(t => s.add(t)));
    return Array.from(s).sort();
  }, [rows]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return rows.filter(r => {
      if (status !== 'ALL' && r.status !== status) return false;
      if (direction !== 'ALL' && r.direction !== direction) return false;
      if (tag !== 'ALL' && !(r.tags || []).includes(tag)) return false;
      if (!needle) return true;
      // Search the whole entry, not just the name — the thing you remember
      // about a signal six months later is usually a phrase in its rules.
      const hay = [r.name, r.summary || '', (r.tags || []).join(' '), JSON.stringify(r.payload || {})]
        .join(' ').toLowerCase();
      return hay.includes(needle);
    });
  }, [rows, q, status, direction, tag]);

  // Keep a selection that still exists; otherwise fall to the first visible row.
  const selected = useMemo(
    () => filtered.find(r => r.id === selectedId) || filtered[0] || null,
    [filtered, selectedId],
  );
  useEffect(() => {
    if (selected && selected.id !== selectedId) setSelectedId(selected.id);
    if (!selected && selectedId !== null) setSelectedId(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected]);

  const strategyOptions = useMemo(
    () => strategies.filter(s => s.id !== 'none').map(s => ({ id: s.id, name: s.name })),
    [],
  );

  const openNew = useCallback(() => { setEditing(null); setEditorOpen(true); }, []);

  /* `/golden-signals?new=1` opens the editor straight away.
   *
   * It is a real convenience - "I have just found something, write it down" is
   * the moment this page exists for, and it is one link from anywhere. It also
   * makes the editor REACHABLE BY THE PROBE: scripts/verify-golden-signals.js
   * may not click anything but the login button (it drives a live trading
   * account), so without a URL that opens it, the largest piece of UI on this
   * page could never be screenshotted or asserted on. */
  useEffect(() => {
    if (new URLSearchParams(location.search).get('new') === '1') openNew();
  }, [location.search, openNew]);
  const openEdit = () => { if (selected) { setEditing(selected); setEditorOpen(true); } };

  const handleSave = async (draft: GoldenSignalDraft) => {
    try {
      if (editing) {
        const saved = await goldenSignalsService.update(editing.id, draft);
        setRows(prev => prev.map(r => (r.id === saved.id ? saved : r)));
        toast.success('Signal saved');
      } else {
        const created = await goldenSignalsService.create(draft);
        setRows(prev => [...prev, created]);
        setSelectedId(created.id);
        toast.success('Signal added to your library');
      }
      setEditorOpen(false);
    } catch (e: any) {
      toast.error(e?.response?.data?.error?.message || 'Failed to save the signal');
    }
  };

  const handleDuplicate = async () => {
    if (!selected) return;
    try {
      const copy = await goldenSignalsService.duplicate(selected.id);
      setRows(prev => [...prev, copy]);
      setSelectedId(copy.id);
      toast.success('Copied — the copy starts as a DRAFT, because the evidence was measured for the original.');
    } catch (e: any) {
      toast.error(e?.response?.data?.error?.message || 'Failed to duplicate the signal');
    }
  };

  const handleDelete = async () => {
    if (!selected) return;
    const ok = await confirm({
      title: `Delete "${selected.name}"?`,
      body: selected.builtinKey
        ? 'This is a signal that shipped with the app. Deleting it here removes your copy; it will come back the next time this page loads.'
        : 'This cannot be undone. If you only want it out of the way, set its status to RETIRED instead — a signal that did not work is worth keeping.',
      confirmLabel: 'Delete',
      destructive: true,
    });
    if (!ok) return;
    try {
      await goldenSignalsService.remove(selected.id);
      setRows(prev => prev.filter(r => r.id !== selected.id));
      toast.success('Signal deleted');
    } catch (e: any) {
      toast.error(e?.response?.data?.error?.message || 'Failed to delete the signal');
    }
  };

  const handleRestore = async () => {
    if (!selected) return;
    const ok = await confirm({
      title: 'Restore the shipped text?',
      body: 'Your edits to this entry will be replaced by the version that ships with the app. Anything you wrote here is lost.',
      confirmLabel: 'Restore',
      destructive: true,
    });
    if (!ok) return;
    try {
      const restored = await goldenSignalsService.restore(selected.id);
      setRows(prev => prev.map(r => (r.id === restored.id ? restored : r)));
      toast.success('Shipped text restored');
    } catch (e: any) {
      toast.error(e?.response?.data?.error?.message || 'Failed to restore the shipped text');
    }
  };

  const statusColor = (s: string) =>
    s === 'CONFIRMED' ? C.bull : s === 'RETIRED' ? C.bear : s === 'TESTING' ? C.warn : C.neutral;

  return (
    <Box sx={{ width: '100%', p: embedded ? 0 : 2 }}>
      {!embedded && (
        <Typography variant="h5" sx={{ fontWeight: 800, mb: 1 }}>Golden Signals</Typography>
      )}

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 820 }}>
        Your permanent record of signals worth keeping. Each entry spells out the exact conditions that
        produce it, so you can build a strategy out of one or several of them later.{' '}
        <b>This page is a catalogue</b> — it places no orders, runs no scans and arms nothing.
      </Typography>

      {/* ---- filters ---- */}
      <Stack direction="row" spacing={1.25} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
        <TextField
          size="small" placeholder="Search names, rules, evidence…" value={q}
          onChange={e => setQ(e.target.value)} sx={{ flex: '2 1 260px' }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>
            ),
          }}
        />
        <TextField select size="small" label="Status" value={status}
          onChange={e => setStatus(e.target.value)} sx={{ flex: '0 1 150px' }}>
          <MenuItem value="ALL">All statuses</MenuItem>
          {GS_STATUSES.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
        </TextField>
        <TextField select size="small" label="Direction" value={direction}
          onChange={e => setDirection(e.target.value)} sx={{ flex: '0 1 150px' }}>
          <MenuItem value="ALL">All directions</MenuItem>
          {GS_DIRECTIONS.map(d => <MenuItem key={d} value={d}>{d}</MenuItem>)}
        </TextField>
        <TextField select size="small" label="Tag" value={tag}
          onChange={e => setTag(e.target.value)} sx={{ flex: '0 1 160px' }}
          disabled={!allTags.length}>
          <MenuItem value="ALL">All tags</MenuItem>
          {allTags.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
        </TextField>
        <Box sx={{ flex: 1 }} />
        <Tooltip title="Reload">
          <IconButton size="small" onClick={load}><RefreshIcon fontSize="small" /></IconButton>
        </Tooltip>
        <Button size="small" variant="contained" startIcon={<AddIcon />} onClick={openNew}>
          New signal
        </Button>
      </Stack>

      {/* ---- master / detail ---- */}
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <Paper
          variant="outlined"
          sx={{ flex: '1 1 300px', minWidth: 260, maxWidth: { xs: '100%', md: 380 }, overflow: 'hidden' }}
        >
          <Box sx={{ px: 1.5, py: 1 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
              {loading ? 'Loading…' : `${filtered.length} of ${rows.length} signal${rows.length === 1 ? '' : 's'}`}
            </Typography>
          </Box>
          <Divider />
          <List dense disablePadding sx={{ maxHeight: 620, overflowY: 'auto' }}>
            {filtered.map(r => (
              <ListItemButton
                key={r.id}
                selected={selected?.id === r.id}
                onClick={() => setSelectedId(r.id)}
                sx={{ display: 'block', py: 1.1 }}
              >
                <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.35 }}>
                  {r.payload?.mark?.label ? (
                    <Box component="span" sx={{
                      width: 11, height: 11, borderRadius: '2px', flexShrink: 0,
                      bgcolor: r.payload.mark.color, border: '1px solid rgba(128,128,128,0.4)',
                    }} />
                  ) : null}
                  <Typography variant="body2" sx={{ fontWeight: 700, flex: 1, minWidth: 0 }} noWrap>
                    {r.name}
                  </Typography>
                </Stack>
                <Stack direction="row" spacing={0.6} alignItems="center" flexWrap="wrap" useFlexGap>
                  <Chip size="small" label={r.status} title={GS_STATUS_HELP[r.status]}
                    sx={{
                      height: 17, fontSize: '0.6rem', fontWeight: 700,
                      bgcolor: statusColor(r.status), color: C.onSolid,
                    }} />
                  <Chip size="small" variant="outlined" label={r.direction}
                    sx={{ height: 17, fontSize: '0.6rem' }} />
                  {(r.tags || []).slice(0, 2).map(t => (
                    <Chip key={t} size="small" variant="outlined" label={t}
                      sx={{ height: 17, fontSize: '0.6rem' }} />
                  ))}
                </Stack>
              </ListItemButton>
            ))}
          </List>

          {!loading && !filtered.length ? (
            <Box sx={{ p: 2 }}>
              <EmptyState
                size="sm"
                kind={rows.length ? 'search' : 'inbox'}
                title={rows.length ? 'Nothing matches those filters' : 'Your library is empty'}
                description={rows.length
                  ? 'Clear the search or the filters to see the rest.'
                  : 'Add the first signal you want to keep.'}
                action={rows.length ? undefined : { label: 'New signal', onClick: openNew }}
              />
            </Box>
          ) : null}
        </Paper>

        <Box sx={{ flex: '2 1 460px', minWidth: 0 }}>
          {selected ? (
            <>
              <Stack direction="row" spacing={1} sx={{ mb: 1.5 }} flexWrap="wrap" useFlexGap>
                <Button size="small" variant="outlined" startIcon={<EditIcon />} onClick={openEdit}>
                  Edit
                </Button>
                <Button size="small" variant="outlined" startIcon={<ContentCopyIcon />} onClick={handleDuplicate}>
                  Duplicate
                </Button>
                {selected.builtinKey ? (
                  <Tooltip title="Replace your edits with the text that ships with the app.">
                    <Button size="small" variant="outlined" startIcon={<RestoreIcon />} onClick={handleRestore}>
                      Restore shipped text
                    </Button>
                  </Tooltip>
                ) : null}
                <Box sx={{ flex: 1 }} />
                <Button
                  size="small" variant="outlined" color="error"
                  startIcon={<DeleteOutlineIcon />} onClick={handleDelete}
                >
                  Delete
                </Button>
              </Stack>
              <SignalDetail row={selected} />
            </>
          ) : !loading ? (
            <EmptyState
              kind="chart"
              title="No signal selected"
              description="Pick one on the left, or add the first entry to your library."
              action={{ label: 'New signal', onClick: openNew }}
            />
          ) : null}
        </Box>
      </Box>

      <SignalEditorDialog
        open={editorOpen}
        row={editing}
        overlayOptions={CHART_OVERLAY_CATALOGUE}
        strategyOptions={strategyOptions}
        onClose={() => setEditorOpen(false)}
        onSave={handleSave}
      />
    </Box>
  );
};

export default GoldenSignalsPage;
